import { CaretDownFilled, CaretUpFilled, PushpinFilled, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Popover, Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { isNullOrUndefined } from "src/common/utils";
import WarningConfirmation from "src/components/warning-popconfirm";
import { PinnedColumnsInfo } from "../../../../components/authZTable/PinnedColumnsInfo";
import { useIsWarningOmitted } from "src/components/warning-popconfirm/useIsWarningOmitted";

export const TableFilterController = (
  datasource, columns, onSearchOrSortChange, displaySearchInPopup = true,
  isFilteredOrSortedInBackend,
  getPopupContainer,
) => {
  const popupContainer = getPopupContainer ?? (() => document.body);
  const [filteredData, setFilteredData] = useState([]);
  const [searchInputs, setSearchInputs] = useState({});
  const sideBarState = useSelector((rootState) => rootState.appSideBar);
  const isSideBarCollapsed = sideBarState?.isCollapsed ?? false;

  const searchRefs = useRef([]);

  const sortOptions = ["ascend", "descend", null];
  const initialSortState = {
    sortOption: 2, columnName: null, sorter: () => {
    },
  };
  const initialSingleSortState = {
    selected: false, columnName: null, sorter: () => {
    },
  };
  const [sortState, setSortState] = useState(initialSortState);
  const [singleSortState, setSingleSortState] = useState(initialSingleSortState);

  function WrapInWarningConfirmationPopUp({ childrenRenderFunction, column, onOk }) {
    function isCurrentSortInDefaultSort() {
      return getDefaultSortColumnKey() === sortState.columnName;
    }

    const isWarningPermanentlyOmitted = useIsWarningOmitted({
      id: column?.sortWarningConfirmation?.id ?? "",
    });

    if (
      isNullOrUndefined(column.sortWarningConfirmation) ||
      !isCurrentSortInDefaultSort() ||
      isWarningPermanentlyOmitted
    ) {
      return childrenRenderFunction(onOk);
    } else {
      return (
        <WarningConfirmation
          onConfirm={onOk}
          warningId={column.sortWarningConfirmation.id}
          placement={column.sortWarningConfirmation.popOverPlacement}
          okText={"Proceed with sort"}
          getPopupContainer={getPopupContainer ?? (() => document.body)}
        >
          {childrenRenderFunction(null)}
        </WarningConfirmation>
      );
    }
  }

  useEffect(() => {
    if (isFilteredOrSortedInBackend && datasource) setFilteredData(datasource);
    else sortAndFilterData();
  }, [JSON.stringify(datasource)]);

  useEffect(() => {
    let timeout = null;
    if (isFilteredOrSortedInBackend) {
      timeout = setTimeout(() => triggerCallbackFunctionForSortOrFilterChange(), 700);
    } else sortAndFilterData();
    return () => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
    };
  }, [searchInputs, sortState, singleSortState]);

  function getFilteredData() {
    if (!datasource)
      return [];

    let data = [...datasource];
    Object.keys(searchInputs).forEach(column => {
      const input = searchInputs[column];
      data = data.filter(record => {
        const result = input.onFilter(input.value, record, column);
        if (input.value === "" && result === "")
          return true;
        return result;
      });
    });
    return data;
  }

  function triggerCallbackFunctionForSortOrFilterChange(currentFilteredData = null) {
    let filter = null;
    Object.keys(searchInputs).forEach(column => {
      filter = { ...filter, [column]: [searchInputs[column].value] };
    });

    const sorter = {
      order: sortOptions[sortState.sortOption ?? singleSortState.sortOption],
      columnKey: sortState.columnName ?? singleSortState.columnName,
    };

    if (filter !== null || sorter.columnKey !== null)
      onSearchOrSortChange(filter, sorter, currentFilteredData);
  }

  const onFilterGeneric = (value, record, columnName) => {
    if (value === "")
      return true;
    return record[columnName]?.toString().toLowerCase().indexOf(value.toLowerCase()) >= 0;
  };

  const handleSearch = (columnName, value, onFilter) => {
    setSearchInputs((prevState) => (
      {
        ...prevState,
        [columnName]: {
          value: value,
          onFilter: (onFilter) ? onFilter : onFilterGeneric,
        },
      }
    ));
  };

  function currentSelectedSortIsInDescendingOrder(sortState, sortOptions) {
    return sortState.sortOption === sortOptions.length - 2;
  }

  function isThereDefaultSortOptionForAnyColumn() {
    return columns.some((column) => !isNullOrUndefined(column.defaultSortOrder));
  }

  function getDefaultSortColumnKey() {
    return columns.find((column) => !isNullOrUndefined(column.defaultSortOrder))?.key;
  }

  const handleSort = (columnKey, sorter) => {
    if (sortState.sortOption === sortOptions.length - 1 || sortState.columnName !== columnKey)
      setSortState({ sortOption: 0, columnName: columnKey, sorter: sorter });
    else if (currentSelectedSortIsInDescendingOrder(sortState, sortOptions) && isThereDefaultSortOptionForAnyColumn()) {
      setDefaultSortOrder();
    } else {
      setSortState((prevState) => ({
        sortOption: prevState.sortOption + 1,
        columnName: columnKey,
        sorter: sorter,
      }));
    }
    setSingleSortState(initialSingleSortState);
  };

  const handleSingleSort = (columnName, sorter) => {
    if (!singleSortState.selected && singleSortState.columnName !== columnName) {
      setSingleSortState(
        { selected: true, columnName: columnName, sorter: sorter },
      );
    } else {
      setSingleSortState(initialSingleSortState);
    }
    if (isThereDefaultSortOptionForAnyColumn() && singleSortState.selected) {
      setDefaultSortOrder();
    } else {
      setSortState(initialSortState);
    }
  };

  function sortAndFilterData() {
    const filteredData = getFilteredData();
    if (sortOptions[sortState.sortOption] === "ascend")
      setFilteredData(
        filteredData.sort((record1, record2) =>
          sortState.sorter(record1, record2, sortOptions[sortState.sortOption]),
        ),
      );
    else if (sortOptions[sortState.sortOption] === "descend")
      setFilteredData(
        filteredData
          .sort((record1, record2) =>
            sortState.sorter(record1, record2, sortOptions[sortState.sortOption]),
          )
          .reverse(),
      );
    else if (singleSortState.selected) {
      setFilteredData(filteredData.sort(singleSortState.sorter));
    } else setFilteredData(filteredData);
    triggerCallbackFunctionForSortOrFilterChange(filteredData);
  }

  const tableHeaderStyle = {
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    flexDirection: "column",
    minHeight: "4.5em",
  };

  const tableHasSearchBar = columns.some(x => x.onFilter);

  function getColumnKey(column) {
    if (column.key)
      return column.key;
    else if (column.dataIndex)
      return column.dataIndex;
    else
      return undefined;
  }

  const updatedColumns = columns.map((column, index) => {
    const isSortEnabled = column.sorter;
    const isFixedColumn = !!column.fixed;
    const isSearchEnabled = column.onFilter && !column.filters;

    function renderPinIcon() {
      if (isFixedColumn) {
        return (
          <Popover
            content={<PinnedColumnsInfo />}
            trigger="hover"
            placement="left"
            getPopupContainer={popupContainer}
            getTooltipContainer={popupContainer}
          >
            <PushpinFilled
              className="PinFilledColumn"
              aria-label={"pin"}
            />
          </Popover>
        );
      }
      return null;
    }

    if (!isSearchEnabled && !isSortEnabled && !tableHasSearchBar)
      return column;

    const columnKey = getColumnKey(column);
    if (!columnKey)
      return column;

    const isSortedAsc = sortState.columnName === columnKey && sortOptions[sortState.sortOption] === "ascend";
    const isSortedDesc = sortState.columnName === columnKey && sortOptions[sortState.sortOption] === "descend";

    const caretUpStyle = isSortedAsc ? { color: "#49a1ad" } : { color: "#bfbfbf" };
    const caretDownStyle = isSortedDesc ? { color: "#49a1ad" } : { color: "#bfbfbf" };

    const sortTooltip = (isSortedAsc) ? "Click to sort descending" : (isSortedDesc) ? "Click to cancel sorting" : "Click to sort ascending";

    const sortIconStyle = { border: "0px", padding: "0px", fontSize: "1em", marginTop: "10px", marginLeft: "10px" };
    const sortButton = (onClick) => (
      <Tooltip
        title={sortTooltip}
        getPopupContainer={popupContainer}
        getTooltipContainer={popupContainer}
      >
        <Button style={sortIconStyle} onClick={onClick}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <CaretUpFilled style={caretUpStyle} />
            <CaretDownFilled style={caretDownStyle} />
          </div>
        </Button>
      </Tooltip>
    );

    const isSingleSortApplied = singleSortState.columnName === columnKey && singleSortState.selected;
    const singleSortTooltip = isSingleSortApplied
      ? "Click to cancel sorting"
      : column.showSorterTooltip?.title ?? "Click to sort";
    const singleSortButton = (onClick) => (
      <Tooltip
        title={singleSortTooltip}
        getPopupContainer={popupContainer}
        getTooltipContainer={popupContainer}
      >
        <Button
          onClick={onClick}
          style={sortIconStyle}
          className="single-sort-column-icon"
        >
          <div style={{ display: "flex", flexDirection: "column", color: "gray" }}>
            <CaretUpFilled style={isSingleSortApplied ? { color: "#49a1ad" } : {}} />
            <CaretDownFilled style={{ color: "#dddddd" }} />
          </div>
        </Button>
      </Tooltip>
    );

    const searchInputStyle = { boxShadow: "none", height: "32px" };
    const input = (
      <Input
        defaultValue={searchInputs[columnKey]?.value ?? ""}
        onChange={(e) => handleSearch(columnKey, e.target.value, column.onFilter)}
        allowClear={true}
        placeholder={`Search ${column.title}`}
        style={displaySearchInPopup ? { ...searchInputStyle, width: "340px" } : searchInputStyle}
        suffix={<SearchOutlined />}
        ref={(element) => (searchRefs.current[columnKey] = element)}
      />
    );

    const tableHeaderFlexBox = { display: "flex", flexDirection: "row", alignContent: "center" };
    const searchPopoverClassNames = () => {
      if (index !== 0)
        return "table-search-popover";
      else if (isSideBarCollapsed)
        return "table-search-popover first-column sidebar-collapsed";
      else
        return "table-search-popover first-column";
    };
    const updatedTitle = (
      <>
        <div>
          {renderPinIcon()}
          <div
            style={
              displaySearchInPopup
                ? { ...tableHeaderFlexBox, marginBottom: "40px" }
                : tableHeaderFlexBox
            }
          >
            <div style={tableHeaderStyle}>
              <div style={{ lineHeight: "1.5em" }} className={"column-title"}>
                {column.title}
              </div>
            </div>
            {isSearchEnabled && displaySearchInPopup ? (
              <Popover
                placement="bottomLeft"
                trigger="click"
                content={input}
                overlayClassName={searchPopoverClassNames()}
                afterVisibleChange={(visible) => {
                  if (visible) {
                    setTimeout(
                      () => searchRefs.current[columnKey]?.focus({ preventScroll: true }),
                      100,
                    );
                  }
                }}
                getPopupContainer={popupContainer}
                getTooltipContainer={popupContainer}
              >
                <Button
                  style={{ ...sortIconStyle, fontSize: "1.5em" }}
                  aria-label={`${columnKey}-search-button`}
                >
                  <SearchOutlined
                    style={searchInputs[columnKey]?.value ? { color: "#49a1ad" } : {}}
                  />
                </Button>
              </Popover>
            ) : (
              <></>
            )}
            {isSortEnabled ? (
              column.singleSort ? (
                <WrapInWarningConfirmationPopUp
                  column={column}
                  onOk={() => handleSingleSort(columnKey, column.sorter)}
                  childrenRenderFunction={singleSortButton}
                />
              ) : (
                <WrapInWarningConfirmationPopUp
                  column={column}
                  onOk={() => handleSort(columnKey, column.sorter)}
                  childrenRenderFunction={sortButton}
                />
              )
            ) : (
              <></>
            )}
          </div>
        </div>
        {isSearchEnabled && !displaySearchInPopup ? input : <></>}
        {!isSearchEnabled && !displaySearchInPopup ? <div style={{ height: "32px" }} /> : <></>}
      </>
    );

    return {
      ...column,
      searchEnabled: false,
      sortEnabled: false,
      sorter: null,
      title: updatedTitle,
    };
  });

  function setDefaultSortOrder() {
    columns.forEach((column) => {
      if (!isNullOrUndefined(column.defaultSortOrder)) {
        setSortState({
          sortOption: sortOptions.indexOf(column.defaultSortOrder),
          columnName: getColumnKey(column),
          sorter: column.sorter,
        });
      }
    });
  }

  useEffect(setDefaultSortOrder, [JSON.stringify(columns)]);

  return {
    filteredData,
    updatedColumns,
  };
};
